/**
 * API-Client für /api/companies (Firmen-Verwaltung).
 *
 * Für Admin-Ansichten, die firmenübergreifend arbeiten, wird der
 * `X-Company-Id: all`-Header erzwungen, damit User/Standorte aller Firmen
 * geladen werden (unabhängig vom aktiven Firmen-Umschalter).
 */

import { apiFetch } from './api';
import type { CompanyRole } from '../store/auth';

const ALL = { 'x-company-id': 'all' } as const;

export interface ApiCompany {
  id: string;
  name: string;
  joinCode: string;
  memberCount: number;
  locationCount: number;
  createdAt: string;
}

export interface ApiCompanyMember {
  userId: string;
  name: string;
  email: string;
  role: CompanyRole;
}

export const companiesApi = {
  list: (token: string) =>
    apiFetch<{ companies: ApiCompany[] }>('/api/companies', { token }).then((r) => r.companies),

  create: (token: string, name: string) =>
    apiFetch<{ company: ApiCompany }>('/api/companies', { method: 'POST', body: { name }, token }).then(
      (r) => r.company,
    ),

  rename: (token: string, id: string, name: string) =>
    apiFetch<{ company: ApiCompany }>(`/api/companies/${id}`, {
      method: 'PUT',
      body: { name },
      token,
    }).then((r) => r.company),

  rotateCode: (token: string, id: string) =>
    apiFetch<{ company: ApiCompany }>(`/api/companies/${id}/rotate-code`, {
      method: 'POST',
      token,
    }).then((r) => r.company),

  members: (token: string, id: string) =>
    apiFetch<{ members: ApiCompanyMember[] }>(`/api/companies/${id}/members`, { token }).then(
      (r) => r.members,
    ),

  addMember: (token: string, id: string, userId: string, role: CompanyRole) =>
    apiFetch(`/api/companies/${id}/members`, {
      method: 'POST',
      body: { userId, role },
      token,
    }),

  removeMember: (token: string, id: string, userId: string) =>
    apiFetch(`/api/companies/${id}/members/${userId}`, { method: 'DELETE', token }),

  // E-Mail-Versand (v1.4.0): Status + Test-Mail an den Admin selbst.
  mailStatus: (token: string) =>
    apiFetch<{ configured: boolean; from: string; appUrl: string }>('/api/companies/mail-status', { token }),

  mailTest: (token: string) =>
    apiFetch<{ result: 'sent' | 'not-configured' | 'failed'; to: string }>('/api/companies/mail-test', {
      method: 'POST',
      token,
    }),

  // Notfall-Schlüssel für Lohndaten (Admin-Rettung, Phase L).
  escrowStatus: (token: string) => apiFetch<EscrowStatus>('/api/companies/wage-escrow', { token }),

  createEscrow: (token: string, replace: boolean) =>
    apiFetch<EscrowStatus & { escrowKey: string }>('/api/companies/wage-escrow', {
      method: 'POST',
      body: { replace },
      token,
    }),

  rescueWages: (token: string, id: string, escrowKey: string) =>
    apiFetch<{ result: RescueResult }>(`/api/companies/${id}/wage-rescue`, {
      method: 'POST',
      body: { escrowKey },
      token,
    }).then((r) => r.result),
};

export interface EscrowStatus {
  configured: boolean;
  fingerprint: string | null;
  createdAt: string | null;
}

export interface RescueResult {
  envelopes: number;
  resealed: number;
  withoutEscrow: number;
  chefsWithoutKey: string[];
}

/** Alle User firmenübergreifend (Admin) — zum Zuordnen von Mitgliedern. */
export function fetchAllUsersRaw(token: string) {
  return apiFetch<{ users: { id: string; name: string; email: string }[] }>('/api/users', {
    token,
    headers: ALL,
  }).then((r) => r.users);
}

/** Alle Standorte firmenübergreifend (Admin) — zum Verschieben zwischen Firmen. */
export function fetchAllLocationsRaw(token: string) {
  return apiFetch<{
    locations: { id: string; name: string; color: string; companyId: string | null }[];
  }>('/api/locations', { token, headers: ALL }).then((r) => r.locations);
}

/** Standort einer Firma zuordnen (Admin). */
export function moveLocation(token: string, locationId: string, companyId: string) {
  return apiFetch(`/api/locations/${locationId}`, {
    method: 'PUT',
    body: { companyId },
    token,
    headers: ALL,
  });
}
